import { Dialog } from "@base-ui/react/dialog";
import closeIcon from "@loot-os/icons/icons/close";
import type { ComponentProps, ReactElement, ReactNode } from "react";

import { cn } from "../foundations/cn.js";
import { Icon } from "./Icon.js";
import { iconButtonStyles } from "./IconButton.styles.js";
import { MODAL_BACKDROP, modalPopupStyles } from "./Modal.styles.js";

export interface ModalProps {
  /** The heading; it names the dialog. */
  title: string;
  /** One sentence under the title; it describes the dialog. */
  description?: string;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** An element that opens the modal, e.g. a Button. */
  trigger?: ReactElement;
  children?: ReactNode;
  /** A ModalFooter with the actions. */
  footer?: ReactNode;
  /** The close button's name. */
  closeLabel?: string;
  /**
   * For a decision that must be answered (a confirmation that can't be skipped): no close
   * button, and a click outside doesn't close it. Escape still does, unless the footer handles it.
   */
  required?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
}

/**
 * A focused task over a dimmed page: a sheet from the bottom on phones, centered from sm. Focus
 * is trapped inside and returns to the trigger on close.
 */
export function Modal({
  title,
  description,
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  children,
  footer,
  closeLabel = "Close",
  required = false,
  size,
  className,
}: ModalProps) {
  return (
    <Dialog.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={(next) => onOpenChange?.(next)}
      disablePointerDismissal={required}
    >
      {trigger && <Dialog.Trigger render={trigger} />}
      <Dialog.Portal>
        <Dialog.Backdrop className={MODAL_BACKDROP} />
        <Dialog.Popup className={modalPopupStyles({ size, className })}>
          <div
            className={cn(
              "flex items-start justify-between gap-4 px-6 pt-6",
              // With no body, the header keeps the room the body would have given it.
              !children && "pb-6",
            )}
          >
            <div className="flex flex-col gap-1">
              <Dialog.Title className="font-body text-heading-h1 text-text-primary">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="font-body text-body-b1 text-text-secondary">
                  {description}
                </Dialog.Description>
              )}
            </div>
            {!required && (
              <Dialog.Close
                aria-label={closeLabel}
                className={cn(iconButtonStyles({ variant: "ghost", size: "sm" }), "-mt-1 -mr-2")}
              >
                <Icon icon={closeIcon} />
              </Dialog.Close>
            )}
          </div>
          {children && (
            <div className="flex flex-col gap-4 overflow-y-auto px-6 pt-4 pb-6">{children}</div>
          )}
          {footer}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** The modal's actions: the main one last (right on desktop, bottom on phones). */
export function ModalFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "flex flex-col-reverse gap-3 border-t border-border-subtle px-6 py-5 sm:flex-row sm:justify-end",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Closes the modal it sits in; render a Button through it: `<ModalClose render={<Button />}>`. */
export function ModalClose(props: ComponentProps<typeof Dialog.Close>) {
  return <Dialog.Close {...props} />;
}
